import React, { useEffect, useState } from "react";
import { makeStyles, fade } from "@material-ui/core/styles";
import { Avatar, Button, Checkbox, CircularProgress, Dialog, IconButton, InputAdornment, TextField } from "@material-ui/core";
import CloseRoundedIcon from "@material-ui/icons/CloseRounded";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import ShortcutRoundedIcon from "@material-ui/icons/ReplyRounded";
import InsertDriveFileRoundedIcon from "@material-ui/icons/InsertDriveFileRounded";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { formatarNomeOuTelefone, formatarTelefone } from "../../utils/phone";

const VERDE = "#0d9488";

const useStyles = makeStyles(theme => ({
  papel: { borderRadius: 20, width: 480, maxWidth: "calc(100vw - 24px)", maxHeight: "86vh", display: "flex", flexDirection: "column" },
  topo: { display: "flex", alignItems: "center", gap: 12, padding: "18px 18px 10px 22px" },
  icone: {
    width: 40, height: 40, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center",
    color: "#fff", background: `linear-gradient(135deg, #14b8a6, ${VERDE})`, transform: "scaleX(-1)",
  },
  previa: {
    margin: "0 22px 12px", padding: "10px 14px", borderRadius: 12, fontSize: 13, display: "flex", gap: 8, alignItems: "center",
    background: theme.palette.type === "dark" ? "rgba(255,255,255,.05)" : "#f1f5f9", borderLeft: `3px solid ${VERDE}`,
  },
  previaTexto: { overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" },
  busca: { margin: "0 22px 8px", "& .MuiOutlinedInput-root": { borderRadius: 12 } },
  lista: { flex: 1, overflowY: "auto", padding: "4px 12px", minHeight: 200 },
  item: {
    display: "flex", alignItems: "center", gap: 12, padding: "8px 10px", borderRadius: 12, cursor: "pointer",
    "&:hover": { background: fade(VERDE, .06) },
  },
  itemAtivo: { background: fade(VERDE, .1) },
  nome: { fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  numero: { fontSize: 12, opacity: .6 },
  rodape: { display: "flex", alignItems: "center", gap: 10, padding: "14px 22px 18px", borderTop: `1px solid ${theme.palette.divider}` },
  botao: { borderRadius: 12, textTransform: "none", fontWeight: 700, padding: "9px 20px" },
  chip: {
    display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, padding: "3px 6px 3px 10px",
    borderRadius: 999, background: fade(VERDE, .12), color: VERDE,
  },
  selecionados: { display: "flex", flexWrap: "wrap", gap: 6, margin: "0 22px 8px" },
}));

const EncaminharModal = ({ aberto, onClose, mensagem }) => {
  const classes = useStyles();
  const [busca, setBusca] = useState("");
  const [contatos, setContatos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [escolhidos, setEscolhidos] = useState([]);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setBusca("");
    setEscolhidos([]);
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return undefined;
    setCarregando(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", { params: { searchParam: busca.trim(), pageNumber: 1 } });
        setContatos((data?.contacts || []).filter(c => c.number || c.isGroup));
      } catch (e) {
        setContatos([]);
      }
      setCarregando(false);
    }, 300);
    return () => clearTimeout(t);
  }, [busca, aberto]);

  const alternar = c => setEscolhidos(lista =>
    lista.find(x => x.id === c.id) ? lista.filter(x => x.id !== c.id) : lista.length >= 20 ? lista : [...lista, c]);

  const enviar = async () => {
    if (!escolhidos.length) return;
    setEnviando(true);
    try {
      const { data } = await api.post(`/messages/${mensagem.id}/encaminhar`, { contactIds: escolhidos.map(c => c.id) });
      const falhas = (data?.resultado || []).filter(r => !r.ok).length;
      if (data?.enviados) toast.success(`Encaminhada para ${data.enviados} contato${data.enviados > 1 ? "s" : ""}.`);
      if (falhas) toast.warn(`${falhas} não ${falhas > 1 ? "foram enviadas" : "foi enviada"}. Confira a conexão.`);
      onClose();
    } catch (e) {
      toastError(e);
    }
    setEnviando(false);
  };

  const temArquivo = !!mensagem?.mediaUrl && mensagem?.mediaType !== "chat";
  const textoPrevia = String(mensagem?.body || "").trim();

  return (
    <Dialog open={!!aberto} onClose={onClose} PaperProps={{ className: classes.papel }}>
      <div className={classes.topo}>
        <span className={classes.icone}><ShortcutRoundedIcon /></span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 17 }}>Encaminhar mensagem</div>
          <div style={{ fontSize: 12.5, opacity: .6 }}>Escolha até 20 contatos</div>
        </div>
        <IconButton onClick={onClose}><CloseRoundedIcon /></IconButton>
      </div>

      <div className={classes.previa}>
        {temArquivo && <InsertDriveFileRoundedIcon style={{ color: VERDE }} fontSize="small" />}
        <span className={classes.previaTexto}>
          {textoPrevia || (temArquivo ? "Arquivo" : "")}
        </span>
      </div>

      <TextField
        className={classes.busca} variant="outlined" size="small" placeholder="Buscar contato por nome ou número"
        value={busca} onChange={e => setBusca(e.target.value)} autoFocus
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon style={{ opacity: .5 }} /></InputAdornment> }}
      />

      {!!escolhidos.length && (
        <div className={classes.selecionados}>
          {escolhidos.map(c => (
            <span key={c.id} className={classes.chip}>
              {formatarNomeOuTelefone ? formatarNomeOuTelefone(c.name, c.number) : c.name}
              <IconButton size="small" style={{ padding: 2, color: VERDE }} onClick={() => alternar(c)}><CloseRoundedIcon style={{ fontSize: 14 }} /></IconButton>
            </span>
          ))}
        </div>
      )}

      <div className={classes.lista}>
        {carregando ? (
          <div style={{ textAlign: "center", padding: 24 }}><CircularProgress size={24} /></div>
        ) : !contatos.length ? (
          <div style={{ textAlign: "center", padding: 24, opacity: .6, fontSize: 13 }}>Nenhum contato encontrado.</div>
        ) : contatos.map(c => {
          const ativo = !!escolhidos.find(x => x.id === c.id);
          return (
            <div key={c.id} className={`${classes.item} ${ativo ? classes.itemAtivo : ""}`} onClick={() => alternar(c)}>
              <Checkbox checked={ativo} color="primary" size="small" style={{ padding: 4 }} />
              <Avatar src={c.profilePicUrl} style={{ width: 36, height: 36 }}>{String(c.name || "?").charAt(0)}</Avatar>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className={classes.nome}>{c.name || c.number}</div>
                <div className={classes.numero}>{c.isGroup ? "Grupo" : formatarTelefone ? formatarTelefone(c.number) : c.number}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className={classes.rodape}>
        <div style={{ flex: 1, fontSize: 13, opacity: .7 }}>
          {escolhidos.length ? `${escolhidos.length} selecionado${escolhidos.length > 1 ? "s" : ""}` : "Nenhum selecionado"}
        </div>
        <Button className={classes.botao} onClick={onClose} disabled={enviando}>Cancelar</Button>
        <Button className={classes.botao} variant="contained" color="primary" disableElevation disabled={!escolhidos.length || enviando} onClick={enviar}>
          {enviando ? <CircularProgress size={18} color="inherit" /> : "Encaminhar"}
        </Button>
      </div>
    </Dialog>
  );
};

export default EncaminharModal;
